import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

import { faBan, faPause, faPlaneDeparture } from "@fortawesome/pro-solid-svg-icons";

import { ListPersonSchedule } from "@/pages/Private/pages/List/schema/list-person-schedule.schema";

import { getLeadHoldState } from "../utils/getLeadHoldState";
import { InfoTag } from "./InfoTag";

const formatDay = (iso: string): string => new Date(iso).toLocaleDateString();

/**
 * Why this lead's outreach is standing still. Without it the two decisions the backend takes on a
 * lead's behalf — stopping it because a colleague answered (PREC-3855/PREC-3895) and moving its
 * next step out because the lead is away (PREC-3894) — leave a row that reads as ordinarily
 * scheduled, or simply finished, while nothing is being sent. That is the question support cannot
 * answer from the UI.
 */
export const LeadHoldTag: FunctionComponent<{ schedules?: ListPersonSchedule[] }> = ({
	schedules,
}) => {
	const { t } = useTranslation();
	const holdState = getLeadHoldState(schedules);

	if (!holdState) {
		return null;
	}

	if (holdState.kind === "paused") {
		return <InfoTag icon={faPause} label={t("outbox.hold.paused")} style="info" />;
	}

	if (holdState.kind === "outOfOffice") {
		const date = formatDay(holdState.until);

		return (
			<InfoTag
				icon={faPlaneDeparture}
				label={holdState.ended ? t("outbox.hold.awayEnded") : t("outbox.hold.away", { date })}
				style={holdState.ended ? "info" : "warning"}
				tooltip={
					holdState.ended
						? t("outbox.hold.awayEndedTooltip", { date })
						: t("outbox.hold.awayTooltip", { date })
				}
			/>
		);
	}

	return (
		<InfoTag
			icon={faBan}
			label={t("outbox.hold.stopped")}
			style="info"
			tooltip={t("outbox.hold.stoppedTooltip", { date: formatDay(holdState.stoppedAt) })}
		/>
	);
};
